<template>
  <ContentWrap>
    <el-form :inline="true" :model="queryParams" class="-mb-15px">
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" clearable placeholder="全部" class="!w-140px">
          <el-option label="申请中" value="applying" />
          <el-option label="在职" value="active" />
          <el-option label="停用" value="disabled" />
          <el-option label="已驳回" value="rejected" />
        </el-select>
      </el-form-item>
      <el-form-item label="姓名">
        <el-input v-model="queryParams.driverName" clearable class="!w-140px" @keyup.enter="handleQuery" />
      </el-form-item>
      <el-form-item label="手机">
        <el-input v-model="queryParams.driverPhone" clearable class="!w-140px" @keyup.enter="handleQuery" />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>
  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column label="编号" prop="id" width="80" />
      <el-table-column label="会员ID" prop="bindMemberId" width="100" />
      <el-table-column label="姓名" prop="driverName" width="100" />
      <el-table-column label="手机" prop="driverPhone" width="120" />
      <el-table-column label="车牌" prop="plateNo" width="110" />
      <el-table-column label="状态" prop="status" width="100">
        <template #default="{ row }">
          {{ statusText(row.status) }}
        </template>
      </el-table-column>
      <el-table-column label="驳回原因" prop="rejectReason" min-width="140" show-overflow-tooltip />
      <el-table-column label="申请时间" min-width="170">
        <template #default="{ row }">{{ formatDateTime(row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="240" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row)">证件</el-button>
          <template v-if="row.status === 'applying'">
            <el-button link type="success" @click="handleApprove(row.id)">通过</el-button>
            <el-button link type="danger" @click="openReject(row)">驳回</el-button>
          </template>
          <el-button v-if="row.status === 'active'" link type="warning" @click="handleDisable(row.id)">
            停用
          </el-button>
          <el-button v-if="row.status === 'disabled'" link type="success" @click="handleEnable(row.id)">
            启用
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="detailVisible" title="司机证件资料" width="720px">
    <el-descriptions :column="2" border>
      <el-descriptions-item label="姓名">{{ detail?.driverName }}</el-descriptions-item>
      <el-descriptions-item label="手机">{{ detail?.driverPhone }}</el-descriptions-item>
      <el-descriptions-item label="车牌">{{ detail?.plateNo }}</el-descriptions-item>
      <el-descriptions-item label="状态">{{ statusText(detail?.status) }}</el-descriptions-item>
    </el-descriptions>
    <div class="mt-16px grid grid-cols-2 gap-12px">
      <div>
        <div class="mb-8px text-14px text-[var(--el-text-color-secondary)]">身份证正面</div>
        <el-image
          v-if="detail?.idCardFrontUrl"
          :src="detail.idCardFrontUrl"
          :preview-src-list="previewList"
          fit="contain"
          class="h-180px w-full rounded border"
        />
      </div>
      <div>
        <div class="mb-8px text-14px text-[var(--el-text-color-secondary)]">身份证反面</div>
        <el-image
          v-if="detail?.idCardBackUrl"
          :src="detail.idCardBackUrl"
          :preview-src-list="previewList"
          fit="contain"
          class="h-180px w-full rounded border"
        />
      </div>
      <div>
        <div class="mb-8px text-14px text-[var(--el-text-color-secondary)]">驾驶证主页</div>
        <el-image
          v-if="detail?.licenseFrontUrl"
          :src="detail.licenseFrontUrl"
          :preview-src-list="previewList"
          fit="contain"
          class="h-180px w-full rounded border"
        />
      </div>
      <div>
        <div class="mb-8px text-14px text-[var(--el-text-color-secondary)]">驾驶证副页</div>
        <el-image
          v-if="detail?.licenseBackUrl"
          :src="detail.licenseBackUrl"
          :preview-src-list="previewList"
          fit="contain"
          class="h-180px w-full rounded border"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import * as Api from '@/api/merchant/groupbuy'
import { formatDate } from '@/utils/formatTime'

defineOptions({ name: 'MerchantGroupbuyDriver' })

/** 毫秒时间戳 → 可读时间；失败显示 '-'，不回退成数字 */
const formatDateTime = (val?: string | number | Date | number[]) => {
  if (val === undefined || val === null || val === '') return '-'
  return formatDate(val as any) || '-'
}

const message = useMessage()
const loading = ref(false)
const list = ref<Api.GroupbuyDriverVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  driverName: undefined as string | undefined,
  driverPhone: undefined as string | undefined,
  status: undefined as string | undefined
})

const detailVisible = ref(false)
const detail = ref<Api.GroupbuyDriverVO>()

const statusText = (status?: string) => {
  const map: Record<string, string> = {
    applying: '申请中',
    active: '在职',
    disabled: '停用',
    rejected: '已驳回'
  }
  return status ? map[status] || status : '-'
}

const previewList = computed(() => {
  if (!detail.value) return []
  return [
    detail.value.idCardFrontUrl,
    detail.value.idCardBackUrl,
    detail.value.licenseFrontUrl,
    detail.value.licenseBackUrl
  ].filter(Boolean) as string[]
})

const getList = async () => {
  loading.value = true
  try {
    const data = await Api.getDriverPage(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}
const openDetail = (row: Api.GroupbuyDriverVO) => {
  detail.value = { ...row }
  detailVisible.value = true
}
const handleApprove = async (id: number) => {
  await message.confirm('确认通过该司机申请？')
  await Api.approveDriver(id)
  message.success('已通过')
  await getList()
}
const openReject = async (row: Api.GroupbuyDriverVO) => {
  const { value } = await ElMessageBox.prompt('请输入驳回原因', '驳回申请', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputPattern: /\S+/,
    inputErrorMessage: '原因不能为空'
  })
  await Api.rejectDriver(row.id!, value)
  message.success('已驳回')
  await getList()
}
const handleDisable = async (id: number) => {
  await message.confirm('确认停用该司机？停用后不可派批。')
  await Api.disableDriver(id)
  message.success('已停用')
  await getList()
}
const handleEnable = async (id: number) => {
  await message.confirm('确认启用该司机？启用后可再次派批。')
  await Api.enableDriver(id)
  message.success('已启用')
  await getList()
}

onMounted(getList)
</script>
